import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Database, UserCheck, FileText, Truck, RotateCcw, Calendar, Phone, Mail } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Privacy Policy | Strum Music";
  }, []);

  const sideNavItems = [
    {
      title: 'Privacy Policy',
      href: '/privacy-policy',
      icon: <Shield className="h-4 w-4" />
    },
    {
      title: 'Terms & Conditions',
      href: '/terms-conditions',
      icon: <FileText className="h-4 w-4" />
    },
    {
      title: 'Shipping Policy',
      href: '/shipping-policy',
      icon: <Truck className="h-4 w-4" />
    },
    {
      title: 'Return Policy',
      href: '/return-exchange-policy',
      icon: <RotateCcw className="h-4 w-4" />
    },
    {
      title: 'Cancellation Policy',
      href: '/cancellation-policy',
      icon: <FileText className="h-4 w-4" />
    },
    {
      title: 'Rental Policy',
      href: '/rental-policy',
      icon: <Calendar className="h-4 w-4" />
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-blue-700 py-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Shield className="h-16 w-16 mx-auto mb-4 text-blue-200" />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-12">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Navigation */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-24 bg-gray-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Policies</h2>
                <nav className="space-y-2">
                  {sideNavItems.map((item, index) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={index}
                        to={item.href}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-700'
                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                        }`}
                      >
                        {item.icon}
                        <span>{item.title}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-8"
              >
                {/* Introduction */}
                <div>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    At Strum Music, we are committed to protecting your privacy and ensuring the security of your personal information. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                    or make a purchase from us.
                  </p>
                  <p className="text-gray-600 text-sm">
                    <strong>Last updated:</strong> January 2025
                  </p>
                </div>

                {/* Information We Collect */}
                <div>
                  <div className="flex items-center mb-4">
                    <Database className="h-6 w-6 text-blue-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-800 mb-3">Personal Information</h3>
                      <p className="text-blue-700 mb-3">We may collect the following personal information when you:</p>
                      <ul className="space-y-2 text-blue-700">
                        <li>• <strong>Create an account:</strong> Name, email address, phone number</li>
                        <li>• <strong>Make a purchase:</strong> Billing and shipping address, payment information</li>
                        <li>• <strong>Contact us:</strong> Name, email, phone number, message content</li>
                        <li>• <strong>Subscribe to newsletter:</strong> Email address, preferences</li>
                        <li>• <strong>Leave reviews:</strong> Name, review content, optional photos</li>
                      </ul>
                    </div>

                    <div className="bg-green-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-3">Automatically Collected Information</h3>
                      <ul className="space-y-2 text-green-700">
                        <li>• IP address and browser information</li>
                        <li>• Device type and operating system</li>
                        <li>• Pages visited and time spent on our website</li>
                        <li>• Referring website and search terms used</li>
                        <li>• Cookies and similar tracking technologies</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* How We Use Information */}
                <div>
                  <div className="flex items-center mb-4">
                    <Eye className="h-6 w-6 text-green-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
                  </div>
                  
                  <div className="bg-green-50 p-6 rounded-lg">
                    <p className="text-green-700 mb-4">We use the information we collect to:</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-green-700">
                        <li>• Process and fulfill your orders</li>
                        <li>• Communicate about your purchases</li>
                        <li>• Provide customer support</li>
                        <li>• Send promotional emails (with consent)</li>
                        <li>• Improve our website and services</li>
                      </ul>
                      <ul className="space-y-2 text-green-700">
                        <li>• Prevent fraud and enhance security</li>
                        <li>• Comply with legal obligations</li>
                        <li>• Analyze website usage and trends</li>
                        <li>• Personalize your shopping experience</li>
                        <li>• Process returns and exchanges</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Information Sharing */}
                <div>
                  <div className="flex items-center mb-4">
                    <UserCheck className="h-6 w-6 text-purple-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Information Sharing</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700">
                      We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                    </p>
                    
                    <div className="bg-purple-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-purple-800 mb-3">Service Providers</h3>
                      <ul className="space-y-2 text-purple-700">
                        <li>• <strong>Payment processors:</strong> Razorpay for secure payment processing</li>
                        <li>• <strong>Shipping partners:</strong> Shiprocket and courier companies for order delivery</li>
                        <li>• <strong>Email services:</strong> For sending order confirmations and newsletters</li>
                        <li>• <strong>Analytics providers:</strong> To understand website usage (anonymized data)</li>
                      </ul>
                    </div>

                    <div className="bg-orange-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-orange-800 mb-3">Legal Requirements</h3>
                      <p className="text-orange-700">
                        We may disclose your information if required by law, court order, or government regulation, 
                        or to protect our rights, property, or safety.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Data Security */}
                <div>
                  <div className="flex items-center mb-4">
                    <Lock className="h-6 w-6 text-red-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
                  </div>
                  
                  <div className="bg-red-50 p-6 rounded-lg">
                    <p className="text-red-700 mb-4">
                      We implement appropriate technical and organizational measures to protect your personal information:
                    </p>
                    <ul className="space-y-2 text-red-700">
                      <li>• SSL encryption for all data transmission</li>
                      <li>• Secure payment processing through PCI DSS compliant providers</li>
                      <li>• Regular security audits and updates</li>
                      <li>• Access controls and employee training</li>
                      <li>• Data backup and recovery procedures</li>
                    </ul>
                  </div>
                </div>

                {/* Your Rights */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Access & Correction</h3>
                      <p className="text-gray-700 text-sm">
                        You can access and update your personal information through your account settings 
                        or by contacting us directly.
                      </p>
                    </div>
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Data Deletion</h3>
                      <p className="text-gray-700 text-sm">
                        You can request deletion of your personal information, subject to legal and 
                        business requirements.
                      </p>
                    </div>
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Marketing Opt-out</h3>
                      <p className="text-gray-700 text-sm">
                        You can unsubscribe from marketing emails at any time using the unsubscribe 
                        link in our emails.
                      </p>
                    </div>
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Data Portability</h3>
                      <p className="text-gray-700 text-sm">
                        You can request a copy of your personal information in a structured, 
                        machine-readable format.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cookies */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking</h2>
                  
                  <div className="bg-yellow-50 p-6 rounded-lg">
                    <p className="text-yellow-700 mb-4">
                      We use cookies and similar technologies to enhance your browsing experience:
                    </p>
                    <ul className="space-y-2 text-yellow-700">
                      <li>• <strong>Essential cookies:</strong> Required for website functionality</li>
                      <li>• <strong>Analytics cookies:</strong> Help us understand how you use our website</li>
                      <li>• <strong>Marketing cookies:</strong> Used to show relevant advertisements</li>
                      <li>• <strong>Preference cookies:</strong> Remember your settings and preferences</li>
                    </ul>
                    <p className="text-yellow-700 mt-4">
                      You can control cookies through your browser settings, but some website features may not work properly if cookies are disabled.
                    </p>
                  </div>
                </div>

                {/* Data Retention */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
                  
                  <div className="bg-indigo-50 p-6 rounded-lg">
                    <p className="text-indigo-700 mb-4">We retain your personal information for as long as necessary to:</p>
                    <ul className="space-y-2 text-indigo-700">
                      <li>• Provide our services and support</li>
                      <li>• Comply with legal obligations</li>
                      <li>• Resolve disputes and enforce agreements</li>
                      <li>• Improve our products and services</li>
                    </ul>
                    <p className="text-indigo-700 mt-4">
                      When information is no longer needed, we securely delete or anonymize it.
                    </p>
                  </div>
                </div>

                {/* Third-Party Links */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-gray-700">
                      Our website may contain links to third-party websites. We are not responsible for the privacy 
                      practices of these external sites. We encourage you to review their privacy policies before 
                      providing any personal information.
                    </p>
                  </div>
                </div>

                {/* Children's Privacy */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
                  
                  <div className="bg-pink-50 p-6 rounded-lg">
                    <p className="text-pink-700">
                      Our services are not intended for children under 13 years of age. We do not knowingly collect 
                      personal information from children under 13. If you are a parent or guardian and believe your 
                      child has provided us with personal information, please contact us immediately.
                    </p>
                  </div>
                </div>

                {/* Changes to Privacy Policy */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
                  
                  <div className="bg-teal-50 p-6 rounded-lg">
                    <p className="text-teal-700">
                      We may update this Privacy Policy from time to time. We will notify you of any changes by 
                      posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage 
                      you to review this Privacy Policy periodically for any changes.
                    </p>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Questions About This Privacy Policy?</h2>
                  <p className="text-gray-700 mb-4">
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4" />
                      <span><strong>Email:</strong> strum.music.co@gmail.com</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4" />
                      <span><strong>Phone:</strong> +91-8076883098</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="h-4 w-4 mt-0.5">🏪</div>
                      <span><strong>Address:</strong> C-7/13, Lawrence Road, Keshav Puram, Delhi 110035</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;